/**
 * MissionVision.js
 * Mission and Vision Statement page for Constellation KG
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import MarketingFooter from '../common/MarketingFooter/MarketingFooter';
import './MissionVision.css';

const MissionVision = () => {
  const navigate = useNavigate();

  return (
    <div className="mission-vision">
      {/* Back Navigation */}
      <div className="mv-back-nav">
        <button 
          className="mv-back-button"
          onClick={() => navigate('/')}
        >
          <ArrowLeft size={20} />
          Back to Home
        </button>
      </div>

      {/* Mission Section */}
      <section className="mv-section">
        <h1 className="mv-title">Our Mission</h1>
        <p className="mv-lead">
          Our mission is to redefine the relationship between people and artificial intelligence.
        </p>
        <p className="mv-body">
          Today's artificial intelligences are remarkable technological achievements but they have evolved independent of our experiences as people. Any 
contextual understanding than an AI has of us as users is necessarily fragmentary—a question here, a conversation there. That fragmentation is likely (and 
justifiably) exacerbated by common concerns about our personal privacy as users.
        </p>
        <p className="mv-body">
          Constellation KG is different. We are not an artificial intelligence. Rather, we are helping people construct a "trust layer" between themselves and 
the AIs they use. By creating Knowledge Entries in our browser extension or jotting thoughts down in My Journal, you are laying the foundation for a more 
personalized AI experience that is designed to respect and protect your privacy.
        </p>
        <p className="mv-body">
          The result: when you interact with an AI through Constellation KG, the AI is given a rich and nuanced understanding of you as reflected in your work 
without gaining access to your personal information.
        </p>
        <p className="mv-body mv-emphasis">
          We are fiercely and unapologetically protective of our users' <button onClick={() => navigate('/privacy')} 
className="mv-inline-link">privacy</button>.
        </p>
        <p className="mv-tagline">
          Your thoughts are yours. We keep them that way.
        </p>
      </section>

      {/* Vision Section */}
      <section className="mv-section">
        <h1 className="mv-title">Our Vision</h1>
        <p className="mv-lead">
          We envision a world where people trust the artificial intelligences that they use.
        </p>
        <p className="mv-body">
          To us, that trust starts with transparency…and giving you a greater degree of control over how the AI frames its interactions with you.
        </p>
        <p className="mv-body">
          We are all awash in seas of information (and misinformation, disinformation, and AI-generated noise). We can all easily discern the information, 
insights, and ideas that resonate most strongly with our own work, thinking, and sense of self.
        </p>
        <p className="mv-body">
          Constellation KG is a safe harbor: we use artificial intelligence to help you identify, explore, and expand upon patterns in your thinking. As a 
result, your work in CKG exists beyond the distortive effects of search engine optimization, paid placement, and large language models hiding layers of 
uncertainty (What information was used to create the LLM? How was that information processed? How was the AI trained?).
        </p>
        <p className="mv-body">
          We do not attempt to fix the structural design challenges associated today's artificial intelligences, rather we have designed Constellation KG to 
align the artificial intelligence's behavior with your priorities by grounding its analyses in the information you've captured and the topics that you've 
reflected on.
        </p>
      </section>

      {/* Footer */}
      <MarketingFooter />
    </div>
  );
};

export default MissionVision;
